import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';

// ============================================================
// CLOUDY LOGO — Composant React avec système de moods & animations
// ============================================================
//
// Usage basique :
//   <CloudyLogo size={48} theme="dark" />
//
// Avec mood contrôlé :
//   <CloudyLogo mood="happy" float />
//
// Avec séquences impératives :
//   const logoRef = useRef();
//   logoRef.current.playSequence([
//     'idle:2000',
//     'blink',
//     'looking-right:1000',
//     'happy:3000',
//     'idle',
//   ]);
//
// Props :
//   size       - Largeur en px (défaut: 48)
//   theme      - 'dark' (nuage bleu) ou 'light' (nuage blanc) — défaut: 'dark'
//   mood       - Mood initial ou contrôlé (défaut: 'idle')
//   float      - Active le flottement idle (défaut: false)
//   shadow     - Affiche l'ombre sous le nuage en mode float (défaut: true)
//   className  - Classe CSS additionnelle
//   style      - Style inline additionnel
//
// Moods disponibles :
//   'idle'           — Yeux E1, regard légèrement vers le haut (défaut)
//   'blink'          — Clignement des deux yeux (~300ms, revient au mood précédent)
//   'wink'           — Clin d'œil droit (~500ms, revient au mood précédent)
//   'happy'          — Yeux contents style D (arcs souriants)
//   'looking-left'   — Pupilles décalées à gauche
//   'looking-right'  — Pupilles décalées à droite
//   'looking-up'     — Pupilles vers le haut
//   'alert'          — Regard direct E6 (pupilles centrées)
//   'surprised'      — Yeux légèrement plus grands
//
// API impérative (via ref) :
//   .setMood(mood)              — Change le mood
//   .getMood()                  — Retourne le mood actuel
//   .playSequence(steps, loop)  — Joue une séquence d'animations
//   .stopSequence()             — Arrête la séquence en cours
//   .blink()                    — Déclenche un clignement
//   .wink()                     — Déclenche un clin d'œil
//
// Format des séquences :
//   ['mood:duration_ms', ...]
//   Exemples :
//     'idle:2000'       → mood idle pendant 2s
//     'blink'           → blink avec durée auto (300ms)
//     'happy:3000'      → happy pendant 3s
//     'idle'            → idle indéfiniment (dernier step)
// ============================================================

// --- CONSTANTES ---

const CLOUD_PATH =
  'M 42,122 C 24,122 10,112 10,98 C 10,82 22,70 36,66 C 36,36 56,12 82,12 C 102,12 116,26 120,42 C 126,36 134,32 144,32 C 162,32 176,46 176,62 C 176,66 175,70 174,72 C 186,78 196,90 196,102 C 196,116 184,122 166,122 Z';

const VIEWBOX = '0 0 200 140';
const VIEWBOX_FLOAT = '0 0 200 150'; // Extra space for shadow when floating

const DEFAULT_COLORS = {
  primary: '#113264',
  white: '#FBFDFF',
};

function getThemeColors() {
  if (typeof document === 'undefined') return DEFAULT_COLORS;
  const styles = getComputedStyle(document.documentElement);
  const primary = styles.getPropertyValue('--light-text-high').trim() || DEFAULT_COLORS.primary;
  const white = styles.getPropertyValue('--light-background').trim() || DEFAULT_COLORS.white;
  return { primary, white };
}

// Position de base des yeux (style E1)
const EYES = {
  left: {
    white: { cx: 88, cy: 82, rx: 9, ry: 10 },
    pupil: { cx: 90, cy: 80, rx: 5.5, ry: 6.5 },
    highlight: { cx: 92.5, cy: 77.5, r: 2.5 },
  },
  right: {
    white: { cx: 130, cy: 80, rx: 9, ry: 10 },
    pupil: { cx: 132, cy: 78, rx: 5.5, ry: 6.5 },
    highlight: { cx: 134.5, cy: 75.5, r: 2.5 },
  },
};

// Happy arcs (style D)
const HAPPY_ARCS = {
  left: 'M80,82 C84,75 92,75 96,82',
  right: 'M122,80 C126,73 134,73 138,80',
};

// Durées auto pour les moods transitoires (ms)
const AUTO_DURATIONS = {
  blink: 300,
  wink: 500,
};

// --- MOOD CONFIGURATIONS ---
// Chaque mood définit les transformations par rapport à l'état idle

const MOOD_CONFIG = {
  idle: {
    leftEyeScale: 1,
    rightEyeScale: 1,
    pupilOffset: { x: 0, y: 0 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
  },
  blink: {
    leftEyeScale: 0,
    rightEyeScale: 0,
    pupilOffset: { x: 0, y: 0 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
    transient: true, // Revient au mood précédent
  },
  wink: {
    leftEyeScale: 1,
    rightEyeScale: 0,
    pupilOffset: { x: 0, y: 0 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
    transient: true,
  },
  happy: {
    leftEyeScale: 0,
    rightEyeScale: 0,
    pupilOffset: { x: 0, y: 0 },
    happyArcsOpacity: 1,
    eyeWhiteScale: 1,
  },
  'looking-left': {
    leftEyeScale: 1,
    rightEyeScale: 1,
    pupilOffset: { x: -4, y: 1.5 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
  },
  'looking-right': {
    leftEyeScale: 1,
    rightEyeScale: 1,
    pupilOffset: { x: 4, y: 1.5 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
  },
  'looking-up': {
    leftEyeScale: 1,
    rightEyeScale: 1,
    pupilOffset: { x: 0, y: -3 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
  },
  alert: {
    leftEyeScale: 1,
    rightEyeScale: 1,
    // Pupilles centrées dans l'œil (E6)
    pupilOffset: { x: -2, y: 2 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1,
  },
  surprised: {
    leftEyeScale: 1,
    rightEyeScale: 1,
    pupilOffset: { x: 0, y: 0 },
    happyArcsOpacity: 0,
    eyeWhiteScale: 1.15,
  },
};

// --- HELPERS ---

function parseStep(step) {
  if (typeof step === 'string') {
    const [mood, durationStr] = step.split(':');
    const duration = durationStr
      ? parseInt(durationStr, 10)
      : AUTO_DURATIONS[mood] || null;
    return { mood, duration };
  }
  return step;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// --- STYLES (CSS-in-JS) ---

const floatKeyframes = `
@keyframes cloudyFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes cloudyShadow {
  0%, 100% { rx: 42; opacity: 0.08; }
  50% { rx: 36; opacity: 0.04; }
}
`;

// --- COMPOSANT PRINCIPAL ---

const CloudyLogo = forwardRef(function CloudyLogo(
  {
    size = 48,
    theme = 'dark',
    mood: moodProp = 'idle',
    float: floatEnabled = false,
    shadow = true,
    className = '',
    style = {},
  },
  ref
) {
  const [currentMood, setCurrentMood] = useState(moodProp);
  const previousMoodRef = useRef('idle');
  const sequenceAbortRef = useRef(null);
  const styleInjectedRef = useRef(false);

  // Inject keyframes once
  useEffect(() => {
    if (styleInjectedRef.current) return;
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('cloudy-logo-keyframes');
    if (!existing) {
      const styleEl = document.createElement('style');
      styleEl.id = 'cloudy-logo-keyframes';
      styleEl.textContent = floatKeyframes;
      document.head.appendChild(styleEl);
    }
    styleInjectedRef.current = true;
  }, []);

  // Sync with prop
  useEffect(() => {
    setCurrentMood(moodProp);
  }, [moodProp]);

  // Handle transient moods (blink, wink) — auto-return
  useEffect(() => {
    const config = MOOD_CONFIG[currentMood];
    if (config?.transient) {
      const duration = AUTO_DURATIONS[currentMood] || 300;
      const timer = setTimeout(() => {
        setCurrentMood(previousMoodRef.current);
      }, duration);
      return () => clearTimeout(timer);
    } else {
      previousMoodRef.current = currentMood;
    }
  }, [currentMood]);

  // Imperative API
  const setMood = useCallback((mood) => {
    if (MOOD_CONFIG[mood]) {
      setCurrentMood(mood);
    } else {
      console.warn(`CloudyLogo: unknown mood "${mood}"`);
    }
  }, []);

  const getMood = useCallback(() => currentMood, [currentMood]);

  const stopSequence = useCallback(() => {
    if (sequenceAbortRef.current) {
      sequenceAbortRef.current.cancelled = true;
      sequenceAbortRef.current = null;
    }
  }, []);

  const playSequence = useCallback(
    async (steps, loop = false) => {
      stopSequence();

      const ctx = { cancelled: false };
      sequenceAbortRef.current = ctx;

      const runOnce = async () => {
        for (let i = 0; i < steps.length; i++) {
          if (ctx.cancelled) return;
          const { mood, duration } = parseStep(steps[i]);
          setCurrentMood(mood);

          if (duration) {
            await delay(duration);
          } else if (AUTO_DURATIONS[mood]) {
            // Transient mood — wait for its auto duration + small buffer
            await delay(AUTO_DURATIONS[mood] + 50);
          } else if (i < steps.length - 1) {
            // Non-last step without duration: default 1s
            await delay(1000);
          }
          // Last step without duration: stays indefinitely
        }
      };

      do {
        await runOnce();
      } while (loop && !ctx.cancelled);

      sequenceAbortRef.current = null;
    },
    [stopSequence]
  );

  const blink = useCallback(() => setMood('blink'), [setMood]);
  const wink = useCallback(() => setMood('wink'), [setMood]);

  useImperativeHandle(
    ref,
    () => ({
      setMood,
      getMood,
      playSequence,
      stopSequence,
      blink,
      wink,
    }),
    [setMood, getMood, playSequence, stopSequence, blink, wink]
  );

  // --- RENDER ---

  const config = MOOD_CONFIG[currentMood] || MOOD_CONFIG.idle;
  const isDark = theme === 'dark';
  const COLORS = getThemeColors();
  const cloudColor = isDark ? COLORS.primary : COLORS.white;
  const detailColor = isDark ? COLORS.white : COLORS.primary;
  const pupilColor = isDark ? COLORS.primary : COLORS.white;
  const highlightColor = isDark ? COLORS.white : COLORS.primary;

  const transitionFast = 'transform 0.15s ease, opacity 0.15s ease';
  const transitionMedium = 'transform 0.3s ease, opacity 0.3s ease';

  // Eye rendering helper
  const renderEye = (side, eyeScale) => {
    const eye = EYES[side];

    return (
      <g key={side}>
        {/* White of the eye */}
        <ellipse
          cx={eye.white.cx}
          cy={eye.white.cy}
          rx={eye.white.rx * config.eyeWhiteScale}
          ry={eye.white.ry * config.eyeWhiteScale}
          fill={detailColor}
          style={{
            transform: `scaleY(${eyeScale})`,
            transformOrigin: `${eye.white.cx}px ${eye.white.cy}px`,
            transition: transitionFast,
          }}
        />
        {/* Pupil + highlight group */}
        <g
          style={{
            transform: `translate(${config.pupilOffset.x}px, ${config.pupilOffset.y}px) scaleY(${eyeScale})`,
            transformOrigin: `${eye.pupil.cx}px ${eye.white.cy}px`,
            transition: `${transitionMedium}, ${transitionFast}`,
          }}
        >
          <ellipse
            cx={eye.pupil.cx}
            cy={eye.pupil.cy}
            rx={eye.pupil.rx}
            ry={eye.pupil.ry}
            fill={pupilColor}
          />
          <circle
            cx={eye.highlight.cx}
            cy={eye.highlight.cy}
            r={eye.highlight.r}
            fill={highlightColor}
          />
        </g>
      </g>
    );
  };

  const viewBox = floatEnabled ? VIEWBOX_FLOAT : VIEWBOX;
  const height = floatEnabled ? 150 : 140;
  const aspectRatio = 200 / height;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size / aspectRatio}
      className={className}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style,
      }}
      role="img"
      aria-label="Cloudy Pocket logo"
    >
      {/* Cloud + eyes group (floats together) */}
      <g
        style={
          floatEnabled
            ? {
                animation: 'cloudyFloat 3s ease-in-out infinite',
              }
            : undefined
        }
      >
        {/* Cloud shape */}
        <path d={CLOUD_PATH} fill={cloudColor} />

        {/* Eyes */}
        {renderEye('left', config.leftEyeScale)}
        {renderEye('right', config.rightEyeScale)}

        {/* Happy arcs (style D) */}
        <path
          d={HAPPY_ARCS.left}
          fill="none"
          stroke={detailColor}
          strokeWidth={3}
          strokeLinecap="round"
          style={{
            opacity: config.happyArcsOpacity,
            transition: transitionFast,
          }}
        />
        <path
          d={HAPPY_ARCS.right}
          fill="none"
          stroke={detailColor}
          strokeWidth={3}
          strokeLinecap="round"
          style={{
            opacity: config.happyArcsOpacity,
            transition: transitionFast,
          }}
        />
      </g>

      {/* Shadow (only when floating) */}
      {floatEnabled && shadow && (
        <ellipse
          cx={104}
          cy={142}
          rx={42}
          ry={4}
          fill={isDark ? `${COLORS.white}26` : `${COLORS.primary}14`}
          style={{
            animation: 'cloudyShadow 3s ease-in-out infinite',
          }}
        />
      )}
    </svg>
  );
});

// --- COMPOSANT LOCKUP (logo + texte) ---

export function CloudyLockup({
  size = 48,
  theme = 'dark',
  layout = 'horizontal', // 'horizontal' | 'vertical'
  fontFamily = "'Nunito', sans-serif",
  fontWeight = 700,
  mood,
  float: floatEnabled,
  logoRef,
  className = '',
  style = {},
}) {
  const isDark = theme === 'dark';
  const COLORS = getThemeColors();
  const textColor = isDark ? COLORS.primary : COLORS.white;
  const fontSize = layout === 'horizontal' ? size * 0.55 : size * 0.4;

  const containerStyle = {
    display: 'inline-flex',
    flexDirection: layout === 'horizontal' ? 'row' : 'column',
    alignItems: 'center',
    gap: layout === 'horizontal' ? size * 0.3 : size * 0.2,
    ...style,
  };

  const textStyle = {
    fontFamily,
    fontWeight,
    fontSize,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: textColor,
    whiteSpace: 'nowrap',
  };

  return (
    <div className={className} style={containerStyle}>
      <CloudyLogo
        ref={logoRef}
        size={size}
        theme={theme}
        mood={mood}
        float={floatEnabled}
      />
      <span style={textStyle}>Cloudy Pocket</span>
    </div>
  );
}

// --- EXPORTS ---

export default CloudyLogo;

// Export constants for external use
export { CLOUD_PATH, EYES, HAPPY_ARCS, DEFAULT_COLORS as COLORS, MOOD_CONFIG, getThemeColors };
